import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchKnockMessages, type RecipientKnock, type RecipientKnockDetail } from "../../lib/api";
import {
  CONNECTED_PREVIEW,
  deskSummary,
  sentNote,
  isHeldSpot,
  knockRefOf,
  OWN_ID,
  SPOT_STOPPED_PREVIEW,
  type Conversation,
} from "../../lib/conversations";
import { doorQuery, doorSettingsQuery, qk } from "../../lib/queries";
import { CloseIcon, ShareIcon, SparkIcon } from "../ui/icons";
import { Button, QuietButton, SecondaryButton } from "../ui/button";
import { XHandleLink } from "../ui/x-link";
import { useOpenedDoors } from "../doorman/door-opened";
import { canonicalUrl } from "../you/door-page";
import {
  BAND_WORD,
  NOT_RECOMMENDED,
  UNRANKED_LABEL,
  decisionOf,
  guardClosedLine,
  nobCallReason,
  notRecommended,
  percentileLabel,
  receiptsLine,
  senderLine,
} from "../inbox/labels";
import { OPEN_THE_DOOR, THROUGH_HINT } from "../inbox/knock-card";
import { ONBOARDING_ASK } from "../doorman/suggestions";
import type { DeskPanel } from "./desk";
import { DoorChip, sentTo } from "./chip";
import { ConversationFace, NobFace, PersonFace } from "./mascot";
import { useShellUi } from "./state";
import { useMetered } from "./use-metered";
import { NO_DOOR_KICKER, NO_DOOR_LINE, TUNE_KICKER, TUNE_LINE } from "./sidebar";
import type { ShellWorld } from "./use-conversations";

export const RAIL_LABEL = "The desk";
export const RAIL_EMPTY_TITLE = "Pick a door";
export const RAIL_EMPTY_LINE = "The paperwork for whatever is open shows up here.";
/** The strip under the card: what this conversation is FOR. */
export const ASK_LABEL = "The ask";
export const TODAY_LABEL = "Today";
export const NO_ASK = "No ask on file.";
/** FR-6.4's summary, when every clause of it is zero. */
export const QUIET_DESK = "Nothing at your door yet.";
export const PERSONALIZE = "Personalize your house rules";
/**
 * WHAT THE BUTTON ACTUALLY ASKS FOR (ruling R-0915-1). One sentence, defined
 * beside the chip that says the same thing (`doorman/suggestions.tsx`), so the
 * button and the chip cannot open two different conversations — and the api
 * derives the run from the same string.
 */
export const PERSONALIZE_ASK = ONBOARDING_ASK;
export const HAND_OUT = "Hand out your door";
export const EDIT_RULES = "Edit";
export const WHAT_GETS_THROUGH = "What gets through";
/**
 * The door has written no public line. Said only when the field is really
 * empty — the owner is told what strangers are told, and a door with a line on
 * it is never described as silent (ruling R-0910-10).
 *
 * WHAT IS EMPTY IS THE PAGE, NOT THE RULES (wave 83). "Your door has not said
 * yet" sat under "In the rules now (v1)" on every door whose rules were in
 * force and whose public line had never been written — the harness's doors,
 * and any door whose owner wrote their rules by hand — and read as "this door
 * has no rules". It had rules; what it lacked was the line strangers read. And
 * "Tell your doorman" was an instruction a Free owner cannot follow.
 */
export const NOTHING_SAID_YET =
  "Your door page doesn't say yet. Your house rules still decide who gets through.";
/** The stamp on a knock the doorman has not published a read on yet (§6.2). */
export const HEARING_STAMP = "Hearing";
/** …and on one the guard stopped before it got there (§8). Not a verdict. */
export const STOPPED_STAMP = "Stopped";

/**
 * The desk beside the conversation.
 *
 * Not a second copy of the conversation, and not a settings panel: it is the
 * paperwork the conversation is about. On somebody's knock that is the verdict,
 * the evidence behind it and the two things you can do; on your own thread it
 * is the door itself — what strangers see, what the rules say, and the two jobs
 * a door needs doing.
 *
 * OUT BY DEFAULT ON A DESK, ABSENT ON A PHONE (owner, 2026-08-25). A desk is
 * wide enough for three columns, and the paperwork beside a knock is the reason
 * somebody opened the knock — arriving to find it folded away made the reader
 * ask for what they had already asked for. So the third column is there when
 * the desk has something in it, and the header's toggle folds it away for
 * anybody who would rather read wide; it toggles in both directions and the
 * choice sticks for the session.
 *
 * A phone has room for one column, so there the desk is a layer over the
 * conversation and stays shut until it is asked for. That split lives in
 * `state.tsx`, which seeds `rightOpen` from the same media query `useDesktop`
 * watches — one question, asked in one place.
 */
export function RightRail({
  world,
  atDoor,
  onAccept,
  onDecline,
  panel,
  busy,
}: {
  world: ShellWorld;
  /**
   * The knock the pane has open, once it has been read — the DETAIL, because
   * the desk's ask strip is the sender's opening line and that lives in the
   * transcript. It is the same object `useActiveKnock` already hands the pane;
   * only the type was narrower than the thing being passed.
   */
  atDoor: RecipientKnockDetail | null;
  onAccept: () => void;
  onDecline: () => void;
  /** The card's open panel, if any — the rail's verbs grey out around it. */
  panel: DeskPanel;
  busy: boolean;
}) {
  const ui = useShellUi();
  const conv = ui.activeId ? world.byId(ui.activeId) : undefined;

  return (
    <aside
      data-pane="rail"
      aria-label={RAIL_LABEL}
      className="flex h-full min-h-0 flex-col border-l border-border bg-bg"
    >
      <header className="nob-header flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border px-4">
        <p className="truncate text-sm font-semibold tracking-tight">
          {!conv
            ? RAIL_LABEL
            : conv.kind === "own-nob"
              ? "Your door"
              : isHeldSpot(conv)
                ? conv.direction === "outgoing"
                  ? // Their handle, and the spot is the reader's — "@maya's held
                    // spot" would name the wrong person's spot entirely.
                    `Your spot at ${conv.name}`
                  : `${firstWord(conv.name)}'s held spot`
                : conv.direction === "outgoing"
                  ? // The knocker's own knock, at their door: "Sam's knock"
                    // read as if the door's owner had done the knocking.
                    `Your knock at ${conv.name}`
                  : `${firstWord(conv.name)}'s knock`}
        </p>
        <button
          type="button"
          aria-label="Close the desk"
          onClick={() => ui.setRightOpen(false)}
          className="nob-press grid size-8 shrink-0 place-items-center rounded-full text-muted
                     hover:bg-hover hover:text-text focus-visible:outline-none focus-visible:ring-2
                     focus-visible:ring-focus/70"
        >
          <CloseIcon className="h-4 w-4" />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {!conv ? (
          <EmptyDesk />
        ) : conv.id === OWN_ID ? (
          <OwnDesk world={world} conv={conv} />
        ) : isHeldSpot(conv) ? (
          <HeldDesk conv={conv} world={world} />
        ) : (
          <KnockDesk
            conv={conv}
            world={world}
            knock={atDoor}
            onAccept={onAccept}
            onDecline={onDecline}
            panel={panel}
            busy={busy}
          />
        )}
      </div>
    </aside>
  );
}

/**
 * The desk with nothing on it.
 *
 * NOT THE COMMON CASE, and it must not be built as though it were: the shell
 * opens on the owner's own thread, so this is what a reader sees when the
 * conversation they had open has LEFT the list — a held spot that became a real
 * knock, a knock that was answered. Before this it was a blank 21rem column,
 * which reads as a broken pane rather than as an empty one.
 */
function EmptyDesk() {
  return (
    <div className="grid h-full place-items-center px-2 text-center">
      <div>
        <NobFace mood="bored" size="desk" className="mx-auto" />
        <p className="nob-empty-title mt-4">{RAIL_EMPTY_TITLE}</p>
        <p className="nob-empty-line mt-1">{RAIL_EMPTY_LINE}</p>
      </div>
    </div>
  );
}

/**
 * WHAT THIS CONVERSATION IS FOR, AND IT IS NOT WHATEVER WAS SAID LAST (F-58).
 *
 * The desk's ask strip used to render `conv.preview`, which is the LIST's line
 * and a running STATE by design: "Hearing in progress", "They passed on this
 * one", and — after a let-in — "They opened the door." So a sender who had just
 * been let through read "THE ASK: They opened the door." on their own desk,
 * which is the product telling somebody that what they wanted was the answer
 * they got.
 *
 * An ask is the OPENING LINE — the words the knock was made with — and it does
 * not move again for the rest of the conversation. Neither wire carries a field
 * for it, so it is read off the transcript: the first thing the sender said, on
 * whichever side of the door the reader is standing.
 *
 *  - AT YOUR OWN DOOR the desk already holds the whole hearing (`useActiveKnock`
 *    reads the detail for the pane beside it), so this costs nothing at all.
 *  - AT SOMEBODY ELSE'S the reader IS the sender, and their own transcript is
 *    an ordinary read of their own knock. Cached under the key it belongs to and
 *    never refetched: an opening line cannot change.
 *
 * `null` until an answer lands, and the strip falls back to `NO_ASK` only for a
 * conversation that really has no sender line in it yet.
 */
function firstSenderLine(lines: { role: string; content: string }[] | undefined): string | null {
  const first = lines?.find((line) => line.role === "sender" && line.content.trim());
  return first ? first.content.trim() : null;
}

function useKnockAsk(conv: Conversation, knock: RecipientKnockDetail | null): string | null {
  const ref = knockRefOf(conv);
  /**
   * The sender's own copy, asked for only when the reader is the sender:
   * `/knock/:id/messages` is a read of one's OWN hearing, and the owner's side
   * of the same conversation is served by the detail already on screen.
   */
  const mine = ref && ref.side === "out" ? ref.knockId : null;
  const sent = useQuery({
    queryKey: qk.knockMessages(mine ?? ""),
    queryFn: () => fetchKnockMessages(mine as string),
    enabled: mine !== null,
    staleTime: Infinity,
    retry: false,
  });

  if (ref?.side === "in") return firstSenderLine(knock?.transcript);
  return firstSenderLine(sent.data?.ok ? sent.data.messages : undefined);
}

/**
 * The card at the top of every desk: who, what state, and what they want.
 *
 * ONE COMPONENT FOR ALL THREE DESKS because it is one object — the reference
 * build draws the same plate over a knock, a held spot and the owner's own
 * door, and the differences between them belong further down the column, not in
 * three near-identical headers that drift apart.
 *
 * The name WRAPS rather than truncates: this is the one place that says who is
 * at the door, and half a name is not context.
 *
 * AND THE HANDLE IS A LINK (owner, 2026-08-25). A desk exists so somebody can
 * decide about a counterpart, and the first thing anyone does to decide about a
 * stranger is look at their profile. Passing `handle` turns the identity into a
 * way there. It is only ever passed for a COUNTERPART: the owner's own desk
 * names the owner, and linking somebody to themselves is furniture.
 */
function DeskCard({
  conv,
  world,
  name,
  handle = null,
  ask,
  askLabel,
  clampAsk = false,
  stamp = null,
  meta = null,
  face = null,
  reopenable = false,
}: {
  conv: Conversation;
  world: ShellWorld;
  name: string;
  /** The counterpart's X handle, when they have one. Email senders do not. */
  handle?: string | null;
  ask: string;
  askLabel: string;
  /**
   * Clamp the ask to a few lines with a "more" to open it (wave 83). Passed on
   * a knock at the reader's own door, where it once pushed the verbs below the
   * fold at 1440×900.
   */
  clampAsk?: boolean;
  stamp?: ReactNode;
  meta?: ReactNode;
  /**
   * The mark in the corner, when the caller knows better than the conversation
   * does. Passed only by `OwnDesk`: the owner's own card names the OWNER, and
   * the doorman's face there is the product's mark standing where the reader's
   * reflection belongs. Every other caller lets the conversation decide.
   */
  face?: ReactNode;
  /**
   * This card is the OWNER'S OWN desk, so a "Paused" chip on it is the way back
   * up (owner item 13). Passed only by `OwnDesk`; a counterpart's card carries
   * no door state of the reader's to press.
   */
  reopenable?: boolean;
}) {
  /**
   * An incoming knock is NAMED by its handle already (`senderName`), so linking
   * the name is the whole job. An outgoing one is named by the door's display
   * name, and the handle is a second fact that needs its own line. One rule,
   * derived rather than configured, so the two cannot be given different
   * answers by two callers.
   */
  const nameIsHandle = handle !== null && handle !== "" && name === `@${handle}`;
  return (
    // `data-paper` is what keeps this readable in the skin, where the pane
    // itself is blue: the card goes back to paper and ink and gains the
    // outline every raised object there wears (styles/tokens.css). It is inert
    // in the two bright themes, which need no such rescue.
    <section data-paper className="overflow-hidden rounded-lg border border-border bg-surface">
      <div className="flex items-start gap-3 px-3 py-3">
        {/* The sender card is about the person — their name, their followers,
            their profile link — so an incoming knock shows the person here even
            while the list row still wears the doorman. */}
        {face ?? (
          <ConversationFace
            conv={conv}
            doorLive={world.doorLive}
            size="lg"
            person={conv.direction === "incoming"}
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            {nameIsHandle && handle ? (
              <XHandleLink
                handle={handle}
                className="text-sm font-semibold leading-tight text-balance"
              />
            ) : (
              <span className="text-sm font-semibold leading-tight text-balance">{name}</span>
            )}
            <DoorChip conv={conv} doorLive={world.doorLive} compact reopenable={reopenable} />
          </p>
          {!nameIsHandle && handle ? (
            <p className="mt-0.5 truncate text-xs text-faint">
              <XHandleLink handle={handle} />
            </p>
          ) : null}
          {meta}
        </div>
        {stamp}
      </div>
      <div className="border-t border-border-soft px-3 py-3">
        <h3 className="text-[11px] font-medium uppercase tracking-[0.12em] text-faint">
          {askLabel}
        </h3>
        {clampAsk ? (
          <ClampedAsk text={ask} />
        ) : (
          <p className="mt-1 text-sm leading-snug text-text-soft">{ask}</p>
        )}
      </div>
    </section>
  );
}

/** Past this many characters the ask folds to four lines behind "more". */
export const ASK_CLAMP_AT = 220;
export const ASK_MORE = "more";
export const ASK_LESS = "less";

function ClampedAsk({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > ASK_CLAMP_AT;
  return (
    <>
      <p
        data-ask-clamped={long && !open ? "" : undefined}
        className={`mt-1 text-sm leading-snug text-text-soft ${long && !open ? "line-clamp-4" : ""}`}
      >
        {text}
      </p>
      {long && (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="mt-1 rounded text-xs text-muted underline underline-offset-4 hover:text-text
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
        >
          {open ? ASK_LESS : ASK_MORE}
        </button>
      )}
    </>
  );
}

/**
 * The verdict, stamped rather than stated.
 *
 * A BAND AND NEVER A NUMBER. The rail already carries the percentile as a Meta
 * row, in the words `labels.ts` chose; this is the same fact at a glance, and
 * putting a score in it would be the one place in the product that prints a raw
 * one. A held spot has no stamp at all, because it earned no judgement (H9).
 *
 * AND A DECISION BEATS A BAND (owner item 9, 2026-08-26). The stamp said the
 * band on everything, so a knock the owner had answered, sent back or saved
 * still read "Middle" — a doorman's stamp reporting what it thought before
 * anybody decided. `decisionOf` is the one derivation the card's pill reads
 * too, so the skin and the two bright themes say the same word.
 */
function StandingStamp({ knock }: { knock: RecipientKnock | null }) {
  if (!knock) return null;
  // A knock with no read on it has earned no band (§6.2): the stamp says what
  // is happening, and never the parser's fallback for a band that is null. A
  // hearing the guard STOPPED is not happening, so it says that instead —
  // "Hearing" over a card that says the doorman stopped is the desk arguing
  // with the pane one column over.
  if (!knock.assessed && !decisionOf(knock)) {
    const word = knock.guard_closed ? STOPPED_STAMP : HEARING_STAMP;
    return (
      <span className="nob-stamp" data-tone="" title={word}>
        {word}
      </span>
    );
  }
  const band = knock.standing.band;
  const decision = decisionOf(knock);
  const word = decision?.word ?? BAND_WORD[band];
  const tone =
    decision?.tone ??
    (band === "TOP_5" || band === "TOP_25" || band === "TIER_STRONG"
      ? "ok"
      : band === "MIDDLE" || band === "TIER_REASONABLE"
        ? ""
        : "bad");
  return (
    <span className="nob-stamp" data-tone={tone} title={word}>
      {word}
    </span>
  );
}

function OwnDesk({ world, conv }: { world: ShellWorld; conv: Conversation }) {
  const ui = useShellUi();
  /**
   * ALL THREE ANSWERS, and `null` is one of them (`use-metered.ts`). Collapsing
   * the unknown into `false` sent a subscriber who pressed this on a cold load
   * to the rules editor instead of to their doorman — the Free road, taken
   * because nobody had said yet. The button waits instead: it is disabled while
   * the tier is unknown, which is a beat, and never the wrong road.
   */
  const metered = useMetered();
  // The same news the pinned row reads (F-28): a door this person was waiting
  // at has opened, and the shelf for it lives in the pane this desk sits over.
  const opened = useOpenedDoors(world.owesDoorStep);
  /**
   * The door's own settings — THE SHELL'S READ, read back from the cache.
   *
   * `hasDoor` is true only once that read has landed, so this asks for nothing
   * a doorless session would be answered 404 for: the world gates the request
   * on the onboarding run (`use-conversations.ts`), and this rail is one of the
   * surfaces that used to fire it a second time off nothing but a handle.
   */
  const { data: settings } = useQuery({
    ...doorSettingsQuery(),
    enabled: world.hasDoor,
    retry: false,
  });
  /**
   * The door plate, for its public line below. Not for the owner's picture:
   * that comes from `/me`, as the sidebar footer's does. Only an owner with a
   * door has a plate to read.
   */
  const { data: door } = useQuery({
    ...doorQuery(world.handle ?? ""),
    enabled: world.hasDoor && Boolean(world.handle),
    retry: false,
  });
  /**
   * WHAT GETS THROUGH IS THE DOOR'S PUBLIC LINE (ruling R-0910-10; gauntlet
   * walk 2026-09-13, U-2).
   *
   * This plate used to read the House Rules and print the bullets under a
   * `## What gets through` heading — a heading `rules/starter.ts` does not
   * write, so on every freshly hung door the owner was told "Your rules have
   * not said yet" while `GET /door/:handle/public` was showing strangers the
   * line they had written in the wizard. Two answers to one question, and the
   * one shown to the owner was the wrong one. It is the same field the public
   * page serves now, so the desk and the door cannot disagree — and the House
   * Rules, which are private without exception, are not excerpted anywhere.
   */
  const publicLine = door?.public_line?.trim() ?? "";

  return (
    <>
      <div className="grid place-items-center py-2">
        <NobFace mood={world.doorLive ? "open" : "watching"} size="desk" />
      </div>

      {/* NO DOOR IS NOT A PAUSED DOOR. Before this branch, a doorless owner's
          desk wore a "Paused" chip and a nob.me URL that led nowhere — the
          same live-inferred-from-the-wrong-fact bug the pane header had. The
          desk now says what is actually true: the step is still owed. Every
          account has a door from the email step on (ruling R-1006-2), so what
          is owed is the Rules step, and the card below it stands beside this. */}
      {(world.owesDoorStep || !world.hasDoor) && (
        <div className="mt-4 rounded-lg border border-dashed border-border px-3 py-3">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-faint">
            {world.owesDoorStep ? TUNE_KICKER : NO_DOOR_KICKER}
          </p>
          <p className="mt-1 text-sm leading-snug text-text-soft">
            {world.owesDoorStep ? TUNE_LINE : NO_DOOR_LINE}
          </p>
          <QuietButton
            type="button"
            className="mt-2 h-auto px-0 text-xs"
            onClick={() => {
              if (!world.owesDoorStep || !ui.setUpDoor) return;
              // News outranks the wizard (F-28): a door they were waiting at
              // has opened, and the shelf saying so is in the pane this desk
              // covers — so the button leads there rather than over it. The
              // rail's equivalent of the pinned row's `open(own)`: make the
              // own thread the thing on screen, desk folded away.
              if (opened.length > 0) {
                ui.setActive(OWN_ID);
                ui.setRightOpen(false);
                return;
              }
              ui.setUpDoor();
            }}
          >
            {world.owesDoorStep ? "Set your rules →" : "Hang your own door →"}
          </QuietButton>
        </div>
      )}

      {/* What a stranger is served when they follow your link, and what the day
          looks like from behind it. A preview, not an editor: everything on it
          is changed in Settings → Your door. */}
      {world.hasDoor && (
        <div className="mt-4">
          <DeskCard
            conv={conv}
            world={world}
            reopenable
            name={world.me?.displayName || conv.name}
            face={
              <PersonFace
                name={world.me?.displayName || conv.name}
                handle={world.handle ?? ""}
                /* The owner's own picture, from `/me` — the read the sidebar
                   footer makes, so the two marks that name the reader are the
                   one cached fact (ruling R-0915-3). */
                avatar={world.me?.avatar ?? null}
                size="lg"
              />
            }
            askLabel={TODAY_LABEL}
            ask={deskSummary(world.conversations) ?? QUIET_DESK}
            meta={
              world.handle ? (
                <p className="mt-0.5 truncate font-mono text-xs text-faint">
                  {canonicalUrl(world.handle)}
                </p>
              ) : null
            }
          />
          {settings?.bio && (
            <p className="mt-2 rounded-md bg-surface px-3 py-2 text-sm leading-snug text-text-soft">
              {settings.bio}
            </p>
          )}
        </div>
      )}

      {world.hasDoor && (
        <section className="mt-5">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.12em] text-faint">
              {WHAT_GETS_THROUGH}
            </h3>
            <QuietButton
              type="button"
              className="h-auto px-0 text-xs"
              onClick={() => ui.openSettings("door", "public-line")}
            >
              {EDIT_RULES}
            </QuietButton>
          </div>
          {publicLine === "" ? (
            <p className="text-sm text-muted">{NOTHING_SAID_YET}</p>
          ) : (
            // Printed on paper in the skin, for the same reason the card above
            // is: these are the door's own words, and they are what the desk is
            // for.
            <p data-paper className="rounded-md bg-surface px-3 py-2 text-sm leading-snug">
              {publicLine}
            </p>
          )}
          {/* TWO ROADS TO THE SAME PLACE, and which one is open depends on
              whether the doorman can do the work (ruling C10). On a plan the
              button SAYS the sentence — it does not draft it and wait for a
              second Enter, which is what the owner reported as the button not
              working. On Free the doorman cannot answer at all, so the rules
              open in the editor instead: personalising them is still possible,
              just by hand. Nobody is sent to a locked composer.

              AND THE FREE ROAD NOW ARRIVES AT THE CONTROLS (owner item 8,
              2026-08-27). It used to open the door page and stop, at the top,
              five blocks above the rules — a button naming one thing landing on
              another. `"rules"` is the panel carrying that intent through, and
              the page scrolls to the three buttons and takes focus. */}
          <button
            type="button"
            data-paper
            className="nob-rail-do"
            disabled={metered === null}
            onClick={() =>
              metered ? ui.askDoorman(PERSONALIZE_ASK, true) : ui.openSettings("door", "rules")
            }
          >
            <SparkIcon className="h-3.5 w-3.5" />
            {PERSONALIZE}
          </button>
        </section>
      )}

      <section className="mt-5">
        <button
          type="button"
          data-paper
          className="nob-rail-do"
          onClick={() => ui.openSheet("share")}
        >
          <ShareIcon className="h-3.5 w-3.5" />
          {HAND_OUT}
        </button>
        <p className="mt-2 text-xs leading-snug text-muted">
          A door nobody has is a door nobody knocks on.
        </p>
      </section>
    </>
  );
}

function KnockDesk({
  conv,
  world,
  knock,
  onAccept,
  onDecline,
  panel,
  busy,
}: {
  conv: Conversation;
  world: ShellWorld;
  knock: RecipientKnockDetail | null;
  onAccept: () => void;
  onDecline: () => void;
  panel: DeskPanel;
  busy: boolean;
}) {
  const outgoing = conv.direction === "outgoing";
  const ref = knockRefOf(conv);
  const opening = useKnockAsk(conv, knock);
  /**
   * AT THE OWNER'S OWN DOOR, THE DOORMAN'S BRIEF WHEN THERE IS ONE (wave 83).
   * The knocker's first message is the ask as it was first put, and it goes
   * stale the moment they follow up — the rail showed "$18.3k MRR" beside a
   * summary that had "$19.6k" and both named references. The brief is updated
   * with every read, so once a read exists it is what the owner is shown.
   */
  const ask = (!outgoing && knock?.assessed ? knock.summary_for_recipient : null) ?? opening;
  const unrecommended = !outgoing && knock !== null && notRecommended(knock);
  const OpenButton = unrecommended ? SecondaryButton : Button;
  /**
   * A knock with no read on it has no standing to print (§6.2) — the desk says
   * so instead of stamping a band it has not got.
   *
   * IT DOES NOT GATE THE VERBS ANY MORE (§3). The owner watching a hearing in
   * flight may open the door, decline or block on what they have read so far;
   * what is withheld is the doorman's judgement, because there is not one yet.
   */
  const assessed = knock?.assessed === true;
  const guardClosed = knock?.guard_closed === true;

  return (
    <>
      <DeskCard
        conv={conv}
        world={world}
        name={conv.name}
        // The counterpart's handle — the sender's on the way in, the door
        // owner's on the way out. Either way it is the other party, and either
        // way it links to their profile.
        handle={conv.handle || null}
        askLabel={ASK_LABEL}
        // The opening line, never the last one — see `useKnockAsk` — or the
        // doorman's brief once there is a read on a knock at the reader's door.
        ask={ask || NO_ASK}
        clampAsk={!outgoing}
        stamp={outgoing ? null : <StandingStamp knock={knock} />}
        meta={
          knock ? (
            <p className="mt-0.5 text-xs leading-snug text-muted">
              {senderLine(
                knock.sender.account_age_days,
                knock.sender.followers,
                knock.sender.company_url,
              )}
            </p>
          ) : null
        }
      />

      {/* The recipient's own reading of the knock — the band, the rank behind
          it, and what the doorman actually checked. None of this exists on the
          sender's side of the wire and none of it is derived here. */}
      {knock ? (
        <>
          {/* No standing and no rank until a read is published (§6.2): neither
              exists yet, and a row that printed the band's fallback would be
              the desk asserting a verdict nobody has reached. */}
          {assessed && <Meta label="Standing" value={BAND_WORD[knock.standing.band]} />}
          {assessed && (
            <Meta
              label="Rank"
              value={percentileLabel(knock.standing.percentile) ?? UNRANKED_LABEL}
            />
          )}
          <Meta label="Stage" value={stageLine(conv)} />
          {/* Only work that HAPPENED is on it: a knock the doorman checked
              nothing for has no receipts, and a heading over an empty line
              would be a promise of content. */}
          {receiptsLine(knock.receipts) && (
            <section className="mt-4">
              <h3 className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-faint">
                Receipts
              </h3>
              <p className="text-sm leading-snug text-text-soft">{receiptsLine(knock.receipts)}</p>
            </section>
          )}
        </>
      ) : (
        <Meta label="Stage" value={stageLine(conv)} />
      )}

      {/* A hearing with no read yet: the doorman is still working, and the desk
          says both halves of that — steer it from the box in the pane, and
          decide whenever you like, because the verbs below are already here.
          The gatekeeper stands at the door and after a decline (§5.2), and
          nowhere else: letting somebody in ends the doorman's phase for good,
          so a let-in row never says it is still hearing them out. */}
      {!outgoing &&
        (conv.stage === "at-door" || conv.stage === "turned-away") &&
        !assessed &&
        !guardClosed && (
          <p className="mt-5 rounded-md bg-surface px-3 py-3 text-sm leading-snug text-muted">
            {IN_FLIGHT_NOTE}
          </p>
        )}

      {/* THE GUARD STOPPED IT (§8), and the recipient still decides. The reason
          is the server's own code, said where it sent one. */}
      {/* …and since R-0925-1 (2) a stop is also Nob's call: the headline sits
          over the guard's own sentence, in one box rather than two. */}
      {!outgoing && guardClosed && (
        <p
          data-nob-call={unrecommended ? "not-recommended" : undefined}
          className="mt-5 rounded-md bg-surface px-3 py-3 text-sm leading-snug text-muted"
        >
          {unrecommended && (
            <span className="block font-medium text-text-soft">{NOT_RECOMMENDED}</span>
          )}
          <span className={unrecommended ? "mt-0.5 block" : undefined}>
            {guardClosedNote(knock?.guard_reason ?? null)}
          </span>
        </p>
      )}

      {/* NOB'S CALL (ruling R-0925-1 (2)): not recommended, and why, in one
          line. A stopped knock says it in the guard's box above. */}
      {unrecommended && !guardClosed && knock?.nob_call && (
        <p
          data-nob-call="not-recommended"
          className="mt-5 rounded-md border border-border-soft bg-surface px-3 py-3 text-sm leading-snug"
        >
          <span className="block font-medium text-text-soft">{NOT_RECOMMENDED}</span>
          <span className="mt-0.5 block text-muted">
            {nobCallReason(knock.nob_call, knock.guard_reason)}
          </span>
        </p>
      )}

      {/* The same two verbs the card carries, offered wherever the card offers
          them: at the door and after a decline, read or no read (§3). The same
          exclusion too — while one of the card's panels is open, the verb that
          did not open it is out of reach here (owner, 2026-08-26). */}
      {!outgoing && (conv.stage === "at-door" || conv.stage === "turned-away") && (
        <div className="mt-5 flex flex-col gap-2">
          {/* The primary on a knock Nob recommended, or has not called yet; an
              outlined button on one it did not — quieter, still one press
              (R-0925-1 (2)). */}
          <OpenButton
            type="button"
            className="h-10 w-full"
            disabled={busy || (panel !== null && panel !== "composerOpen")}
            aria-disabled={busy || (panel !== null && panel !== "composerOpen") || undefined}
            onClick={onAccept}
          >
            {OPEN_THE_DOOR}
          </OpenButton>
          <QuietButton
            type="button"
            className="h-10 w-full"
            disabled={busy || (panel !== null && panel !== "dismissOpen")}
            aria-disabled={busy || (panel !== null && panel !== "dismissOpen") || undefined}
            onClick={onDecline}
          >
            Not for me
          </QuietButton>
          <p className="text-xs leading-snug text-muted">{THROUGH_HINT}</p>
        </div>
      )}

      {/* The sender's own row, still at somebody else's door. Two sentences,
          and the read is what moves between them (ruling R-0925-1 (1)): while
          the hearing runs the knocker is talking to the doorman; once the read
          is published it has gone to the owner, and what happens next is
          theirs. Never "this hearing is over" (§10), and never what the read
          recommended — that is the recipient's to act on. */}
      {outgoing && conv.stage === "at-door" && ref && !conv.connected && (
        <p className="mt-5 rounded-md bg-surface px-3 py-3 text-sm leading-snug text-muted">
          {conv.assessed ? sentNote(conv.name) : OUTGOING_NOTE}
        </p>
      )}

      {/* The pair's fact, said once (§9): a door open both ways is a
          different sentence from "your doorman stepped aside".

          WHOSE DOORMAN IS A FACT OF THE SIDE (wave 75). The owner's own Nob
          stepped aside for the person it let through; the knocker let through
          was never talking to theirs, and read "Your doorman" as if they had
          been. */}
      {conv.connected ? (
        <p className="mt-5 rounded-md bg-surface px-3 py-3 text-sm leading-snug text-muted">
          {CONNECTED_NOTE}
        </p>
      ) : (
        conv.stage === "through" && (
          <p className="mt-5 rounded-md bg-surface px-3 py-3 text-sm leading-snug text-muted">
            {outgoing ? THEIR_DOORMAN_ASIDE : YOUR_DOORMAN_ASIDE}
          </p>
        )
      )}
    </>
  );
}

/**
 * The desk's sentences.
 *
 * IN_FLIGHT_NOTE no longer says the verbs arrive with the verdict, because they
 * do not: they are here already (§3). What it says instead is the one thing the
 * owner cannot do from the desk — steer the questions — and that deciding is
 * theirs whenever they want it.
 */
export const IN_FLIGHT_NOTE =
  "Your Nob is still hearing them out. Talk to it in the conversation to steer the next " +
  "question — or decide now, on what you have read.";
export const OUTGOING_NOTE =
  "You are still talking to their doorman. If they let you through, the conversation becomes theirs.";
/** A let-through thread, on the owner's side: their own doorman stepped aside. */
export const YOUR_DOORMAN_ASIDE = "Your doorman stepped aside. This is the two of you.";
/** The same thread on the knocker's side, whose doorman it was not. */
export const THEIR_DOORMAN_ASIDE = "Their doorman stepped aside. This is the two of you.";
/** Built on the list's own literal, so the row and the desk cannot drift. */
export const CONNECTED_NOTE = `${CONNECTED_PREVIEW} The door is open both ways.`;

/**
 * THE GUARD'S OWN LINE, ON THE OWNER'S DESK (§8).
 *
 * The first half is the card's sentence, from the one bank in `labels.ts`, so
 * the desk and the card cannot describe one stop two ways. The second half is
 * what only the desk says: the decision is still the owner's. It used to open
 * "Nothing was judged", which R-0925-1 (2) made untrue beside the headline
 * over it — a stop is one of the reasons Nob does not recommend a knock.
 */
export const GUARD_STILL_YOURS =
  "The decision is still yours — you can open the door, decline or block.";

export function guardClosedNote(reason: string | null): string {
  return `${guardClosedLine(reason)} ${GUARD_STILL_YOURS}`;
}

/**
 * A held spot's desk (PRD FR-6.3, WR FR-G9.3).
 *
 * TWO METAS AND NO THIRD. `Standing` is absent, and it is absent rather than
 * empty: no standing was earned, so a row reading "Standing —" would still be
 * the product asserting it had looked. Nothing here is derived from a score
 * because there is no score to derive it from, and there are no verbs, because
 * FR-G9.3 gives a held spot no composer — there is nothing to reply to until
 * they write, and the panel is required to say so.
 *
 * BOTH DIRECTIONS ARE THE SAME DESK (owner, 2026-08-27). A spot the reader
 * holds is the identical object seen from the other end of a door that does not
 * exist, and it has the same nothing to say about standing. What changes is the
 * three sentences: whose spot it is, what stage means here, and why there is no
 * box to type in.
 */
function HeldDesk({ conv, world }: { conv: Conversation; world: ShellWorld }) {
  const own = conv.direction === "outgoing";
  return (
    <>
      {/* No stamp, and its absence is the point: a held spot earned no
          judgement, so there is nothing to stamp it with. */}
      <DeskCard
        conv={conv}
        world={world}
        name={conv.name}
        // A held spot is somebody wanting in, and looking them up is the whole
        // of what there is to do here — this desk has no composer.
        handle={conv.handle || null}
        askLabel={own ? SPOT_ASK_LABEL : ASK_LABEL}
        /**
         * THE LINE, AND NOT WHAT BECAME OF THE SPOT (F-27, browser walk
         * 2026-09-13). `preview` on a stopped spot is "You stopped waiting." —
         * the row's sentence, correct in a list and a lie under "Your line",
         * where it printed a system notice as the person's own words. The
         * stopping is said one row down, in Stage, where a state belongs.
         */
        ask={conv.spot?.line || conv.preview || NO_ASK}
      />

      <Meta label="Direction" value={own ? "You want in" : "They want in"} />
      <Meta label="Stage" value={stageLine(conv)} />

      <p className="mt-5 rounded-md bg-surface px-3 py-3 text-sm leading-snug text-muted">
        {own ? SPOT_NO_COMPOSER : HELD_NO_COMPOSER}
      </p>
    </>
  );
}

/** FR-G9.5: the panel states that this becomes a knock when they write. */
export const HELD_NO_COMPOSER =
  "They held a spot before your door opened. There is nothing to reply to yet — " +
  "when they knock for real, this becomes a knock and your Nob hears them out.";

/** The reader's own words, so the desk calls them that rather than "the ask". */
export const SPOT_ASK_LABEL = "Your line";

/** The mirror of FR-G9.5, from the other end: no doorman, so no conversation. */
export const SPOT_NO_COMPOSER =
  "There is no Nob at this handle yet, so there is nobody to talk to. " +
  "The day they set one up you can knock for real, and your line is what they see first.";

/** The Stage row on a spot of the reader's own (owner, 2026-08-27). */
export const SPOT_STAGE = "Nob not set up";

function stageLine(conv: Conversation): string {
  // A spot of the reader's own says what is actually true of it, which is a
  // fact about the OTHER end: there is no doorman at that handle yet (owner,
  // 2026-08-27). "Holding a spot" describes what the reader did; this describes
  // what they are waiting on, and that is what a Stage row is for. The owner's
  // side keeps the other sentence — their Nob is plainly set up, since they are
  // reading this in a console — see `fromMyWaiting`.
  if (conv.stage === "pre-knock") {
    // A spot that was let go of says so HERE, beside the line rather than in
    // place of it (F-27): what became of the spot is a stage, and the sentence
    // they wrote is not.
    if (conv.spot?.stopped) return SPOT_STOPPED_PREVIEW;
    return conv.direction === "outgoing" ? SPOT_STAGE : "Holding a spot";
  }
  // THE FIVE SENTENCES OF §6.4, in the order the gate decides them: the pair's
  // fact first (§9), then the gate, then whether a read has landed.
  if (conv.connected) return "Connected";
  if (conv.stage === "through") return "Let through";
  if (conv.stage === "turned-away") return "Turned away";
  if (conv.inFlight) return "Hearing in progress";
  // The knocker's side past the hearing is the read having gone to the owner
  // (R-0925-1 (1)) — "Still at their door" beside "Waiting on them" and "On
  // them" was three words for it. The one unread outgoing row that is not in
  // flight is a knock just sent, one round trip before the server's own row.
  if (conv.direction === "outgoing") {
    return conv.assessed ? sentTo(conv.name) : "Hearing in progress";
  }
  return "Still at your door";
}

function firstWord(name: string): string {
  return name.trim().split(" ")[0] ?? name;
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="mt-3 flex items-baseline justify-between gap-3 border-b border-border-soft py-2">
      <span className="text-xs text-muted">{label}</span>
      <span className="text-right text-sm font-medium">{value}</span>
    </div>
  );
}
